import { Injectable, Injector } from '@angular/core';
import { IdentityService } from '../identity/identity.service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { Identity } from '../../core/models/identity/identity.model';
import { NavigationExtras, Router } from '@angular/router';
import { OAuthResponse } from '../../core/models/authentication/auth0.model';
import { tap } from 'rxjs/operators';
import { SnackBarService } from '../snackbar.service';
import { Auth0Service } from './auth0/auth0.service';
import { jwtDecode } from 'jwt-decode';
import { AccessJwtPayload, IdentityJwtPayload } from '../../core/models/authentication/authentication.models';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  private readonly refreshTokenRequestsSubject = new Subject<Identity>();
  private refreshTokenSubscription: Subscription;

  constructor(private readonly identityService: IdentityService,
              private readonly auth0Service: Auth0Service,
              private readonly router: Router,
              private readonly injector: Injector
  ) {
  }

  public login(email: string, password: string): Observable<OAuthResponse> {
    return this.auth0Service.getPasswordGrantCredentials(email, password)
      .pipe(tap((response) => {
        this.setIdentity(response);
        this.router.navigate(['']).then();
      }));
  }

  public logout(fallback: { path: string, extra: NavigationExtras } = { path: '', extra: undefined }): void {
    this.identityService.destroyIdentity();
    this.router.navigate([fallback.path], fallback.extra).then();
  }

  public isLoggedIn(): boolean {
    return !!this.identityService.getAccessToken();
  }

  public refreshToken(): Observable<Identity> {
    const snackbarService = this.injector.get(SnackBarService);

    // If a refresh is already pending we don't start a new one and wait for the first one to finish
    if (!this.refreshTokenSubscription) {

      if (this.identityService.getRefreshToken()) {
        this.callRefreshToken(this.identityService.getRefreshToken(), snackbarService);
      } else {
        snackbarService.openSnackBar('AUTHENTICATION.UNAUTHORIZED_USER', false);
        this.logout();
        // In this case we need to notify the subscriber that no refresh will be executed.
        // To do so we send an empty Identity object to prevent it to listen to the subject
        return of<Identity>({});
      }
    }

    // Return the subject as observable
    return this.refreshTokenRequestsSubject.asObservable();
  }

  private setIdentity(oauthResponse: OAuthResponse): void {
    const identityPayload = jwtDecode<IdentityJwtPayload>(oauthResponse.id_token);
    const accessPayload = jwtDecode<AccessJwtPayload>(oauthResponse.access_token);
    this.identityService.setIdentity(
      identityPayload.email,
      oauthResponse.access_token,
      accessPayload.exp * 1000,
      oauthResponse.refresh_token,
      accessPayload.mp_roles
    );
  }

  private callRefreshToken(refreshToken: string, snackbarService: SnackBarService): void {
    this.refreshTokenSubscription = this.auth0Service.refreshToken(refreshToken)
      .subscribe({
        next: (response) => {
          this.refreshIdentity(response);
          this.refreshTokenRequestsSubject.next(this.identityService.getIdentity());
          this.refreshTokenSubscription = undefined;
        },
        error: () => {
          snackbarService.openSnackBar('AUTHENTICATION.UNAUTHORIZED_USER', false);
          this.logout();
          this.refreshTokenRequestsSubject.next({});
          this.refreshTokenSubscription = undefined;
        }
      });
  }

  private refreshIdentity(oAuthResponse: OAuthResponse): void {
    const accessPayload = jwtDecode<AccessJwtPayload>(oAuthResponse.access_token);
    this.identityService.refreshIdentity(
      oAuthResponse.access_token,
      accessPayload.exp * 1000,
      oAuthResponse.refresh_token,
      accessPayload.mp_roles
    );
  }
}
